/// <reference types="vite/client" />
import { useState, useEffect, useRef, ReactNode, lazy, Suspense } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'motion/react';
import { Routes, Route, Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { 
  Building2, 
  MapPin, 
  Calendar, 
  Ruler, 
  Hammer, 
  ChevronRight, 
  Star, 
  Lock, 
  Plus, 
  Trash2, 
  LogOut, 
  ArrowLeft,
  X,
  Phone,
  Mail,
  Menu,
  Instagram,
  Facebook,
  ShieldCheck,
  MessageSquare,
  ArrowUpRight,
  PlusCircle,
  LayoutGrid,
  Image as ImageIcon,
  Edit,
  Calculator,
  HardHat,
  FileText,
  Receipt,
  Scale,
  Target
} from 'lucide-react';
import Masonry from 'react-masonry-css';
import type { User } from 'firebase/auth';
import { responsiveImageProps } from './lib/images';

const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const loadAuth = () => import('./lib/auth');
import { Project, Testimonial, SiteConfig } from './types';

// --- Shared Internal Components ---

const Navbar = ({ onAdminClick }: { onAdminClick: () => void }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleHomeClick = () => {
    if (pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Jornada do Cliente', path: '/jornada' },
    { name: 'Portfólio', path: '/portfolio' },
  ];

  const handleLinkClick = (path: string) => {
    setIsMobileMenuOpen(false);
    if (path === pathname) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-16 border-b transition-all duration-500 ${
          isScrolled 
            ? 'h-16 bg-white/70 backdrop-blur-xl border-gray-100/30 shadow-sm' 
            : 'h-24 bg-white/80 backdrop-blur-2xl border-gray-100/50'
        }`}
      >
        <div 
          className="font-display text-2xl font-extrabold cursor-pointer tracking-tighter"
          onClick={handleHomeClick}
        >
          Berti<span className="text-berti-gold">.</span>
        </div>

        <div className="flex items-center gap-4 md:gap-12">
          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-[11px] font-bold uppercase tracking-[0.2em] transition-all duration-500 cursor-pointer relative group ${
                  pathname === link.path ? 'text-berti-gold' : 'text-berti-ink hover:text-berti-gold'
                }`}
              >
                {link.name}
                <span className={`absolute -bottom-1 left-0 h-[1px] bg-berti-gold transition-all duration-500 ${pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'}`} />
              </Link>
            ))}
            <button 
              onClick={() => {
                document.getElementById('footer')?.scrollIntoView({ behavior: 'smooth' });
              }} 
              className="text-[11px] font-bold uppercase tracking-[0.2em] hover:text-berti-gold transition-all duration-500 cursor-pointer relative group"
            >
              Contato
              <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-berti-gold transition-all duration-500 group-hover:w-full" />
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button 
              onClick={() => window.location.href = `https://wa.me/5541991836651`}
              className={`hidden lg:flex px-8 py-3.5 bg-berti-gold text-berti-ink text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-berti-dark hover:text-white transition-all items-center shadow-lg ${isScrolled ? 'scale-90' : 'scale-100'}`}
            >
              Solicitar Orçamento
            </button>
            <button 
              aria-label="Área do Cliente"
              onClick={onAdminClick}
              className={`px-5 md:px-7 py-3.5 bg-berti-ink text-white hover:bg-berti-dark text-[11px] font-bold uppercase tracking-[0.2em] flex items-center gap-2 transition-all ${isScrolled ? 'scale-90' : 'scale-100'} group`}
            >
              <Lock size={12} className="text-berti-gold group-hover:text-white transition-colors" /> <span className="hidden sm:inline">Área do Cliente</span>
            </button>

            {/* Mobile Menu Toggle */}
            <button 
              aria-label="Abrir menu"
              aria-expanded={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden p-2 text-berti-ink hover:text-berti-gold transition-colors"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-[100] bg-white lg:hidden"
          >
            <div className="flex flex-col h-full">
              <div className="h-20 flex items-center justify-between px-6 border-b border-gray-100">
                <div className="font-display text-2xl font-extrabold tracking-tighter">
                  Berti<span className="text-berti-gold">.</span>
                </div>
                <button 
                  aria-label="Fechar menu"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 text-berti-ink hover:text-berti-gold transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="flex-1 flex flex-col items-center justify-center gap-8 p-12">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => handleLinkClick(link.path)}
                    className={`text-2xl font-display font-medium tracking-tight hover:text-berti-gold transition-colors ${pathname === link.path ? 'text-berti-gold' : 'text-berti-ink'}`}
                  >
                    {link.name}
                  </Link>
                ))}
                <button 
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    document.getElementById('footer')?.scrollIntoView({ behavior: 'smooth' });
                  }} 
                  className="text-2xl font-display font-medium tracking-tight text-berti-ink hover:text-berti-gold transition-colors"
                >
                  Contato
                </button>
              </div>

              <div className="p-12 border-t border-gray-100 flex flex-col items-center gap-6">
                <button 
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    onAdminClick();
                  }}
                  className="w-full py-5 bg-berti-ink text-white text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-berti-dark transition-all flex items-center justify-center gap-3 group"
                >
                  <Lock size={12} className="text-berti-gold group-hover:text-white transition-colors" /> Área do Cliente
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const WhatsAppButton = () => (
  <motion.a
    href={`https://wa.me/5541991836651`}
    target="_blank"
    rel="noopener noreferrer"
    initial={{ opacity: 0, scale: 0.5, y: 20 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    transition={{ delay: 1, duration: 0.5 }}
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
    className="fixed bottom-8 right-8 z-[60] bg-[#25D366] text-white p-4 rounded-full shadow-[0_20px_50px_rgba(37,211,102,0.3)] flex items-center justify-center hover:bg-[#128C7E] transition-all duration-300 group"
    aria-label="Contato via WhatsApp"
  >
    <div className="absolute right-full mr-4 bg-white text-berti-ink text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-2 rounded shadow-xl opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 pointer-events-none whitespace-nowrap border border-gray-100">
      Falar conosco
    </div>
    <MessageSquare size={24} fill="currentColor" className="text-white/20" />
    <div className="absolute inset-0 rounded-full border border-white animate-ping opacity-20 group-hover:hidden" />
  </motion.a>
);

const ParallaxImage = ({ src, alt, className }: { src: string; alt: string; className: string }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1, 1.1, 1]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.9, 1, 1, 0.9]);

  return (
    <div ref={ref} className="relative w-full h-full overflow-hidden bg-gray-100">
      <motion.img
        {...responsiveImageProps(src, "(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw")}
        alt={alt}
        decoding="async"
        loading="lazy"
        style={{ y, scale, opacity }}
        className={`${className} absolute inset-0 contrast-110 saturate-110`}
      />
    </div>
  );
};

const HeroBackground = ({ src, alt, priority = false }: { src: string; alt: string; priority?: boolean }) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 1000], [0, 200]);

  return (
    <motion.img 
      initial={{ scale: 1.1, opacity: priority ? 1 : 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 2, ease: "easeOut" }}
      {...responsiveImageProps(src, "100vw", true)}
      decoding="async"
      loading={priority ? undefined : "lazy"}
      fetchPriority={priority ? "high" : "auto"}
      style={{ y }}
      className="w-full h-full object-cover saturate-110 contrast-105"
      alt={alt}
    />
  );
};

const PageTransition = ({ children }: { children: ReactNode }) => (
  <motion.div
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    className="w-full"
  >
    {children}
  </motion.div>
);

const LoadingSpinner = () => (
  <motion.div 
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="flex items-center justify-center py-24 w-full"
  >
    <div className="w-6 h-6 border-2 border-berti-ink/5 border-t-berti-gold rounded-full animate-spin" />
  </motion.div>
);

const ConceptGallery = ({ photos }: { photos: string[] }) => {
  if (!photos || photos.length === 0) return null;

  return (
    <div className="w-full relative py-20 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-16">
        <div className="text-berti-gold-dark text-[10px] font-bold uppercase tracking-[0.5em] mb-4">Galeria Conceito</div>
        <h3 className="text-4xl md:text-5xl font-display italic text-berti-ink">Da fundação ao acabamento.</h3>
      </div>
      
      <div className="flex overflow-x-auto pb-12 gap-6 px-6 md:px-12 no-scrollbar cursor-grab active:cursor-grabbing snap-x">
        {photos.map((src, i) => (
          <motion.div
            key={i}
            whileHover={{ scale: 1.02 }}
            className="flex-none w-[300px] md:w-[450px] aspect-[4/3] bg-gray-50 overflow-hidden shadow-lg snap-center"
          >
            <img src={src} alt={`Conceito Berti ${i + 1}`} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" loading="lazy" />
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const Footer = ({ onHome }: { onHome: (s: any) => void }) => (
  <footer id="footer" className="bg-berti-dark text-white pt-24 pb-12 px-6 md:px-12 border-t border-white/10">
    <div className="max-w-7xl mx-auto mb-20">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-24">
        <div className="md:col-span-4 space-y-10">
          <Link to="/" className="font-display text-4xl font-extrabold tracking-tighter block">
            Berti<span className="text-berti-gold">.</span>
          </Link>
          <p className="text-white/70 text-base leading-relaxed font-light max-w-xs">
            Gestão e execução de obras de alto padrão com organização, clareza técnica e controle absoluto.
          </p>
        </div>

        <div className="md:col-span-2 space-y-10">
          <h4 className="text-berti-gold text-[10px] font-bold uppercase tracking-widest">Navegação</h4>
          <div className="flex flex-col gap-5 text-sm font-light text-white/50">
            <Link to="/" className="hover:text-berti-gold transition-colors text-left">Início</Link>
            <Link to="/jornada" className="hover:text-berti-gold transition-colors text-left">Jornada do Cliente</Link>
            <Link to="/portfolio" className="hover:text-berti-gold transition-colors text-left">Portfólio</Link>
            <button onClick={() => document.getElementById('footer')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-berti-gold transition-colors text-left">Contato</button>
          </div>
        </div>
        
        <div className="md:col-span-3 space-y-10">
          <h4 className="text-berti-gold text-[10px] font-bold uppercase tracking-widest">Contato</h4>
          <div className="space-y-6">
            <div className="flex items-center gap-4 text-white/80 hover:text-white transition-colors cursor-pointer group" onClick={() => window.location.href=`https://wa.me/5541991836651`}>
              <MessageSquare size={16} className="text-berti-gold" />
              <span className="text-sm font-medium tracking-tight">(41) 99183-6651</span>
            </div>
            <div className="flex items-center gap-4 text-white/80 hover:text-white transition-colors group">
              <Mail size={16} className="text-berti-gold" />
              <span className="text-sm font-medium tracking-tight text-xs lg:text-sm">berti@curitibaconstrutora.com.br</span>
            </div>
          </div>
        </div>

        <div className="md:col-span-3 space-y-10">
          <h4 className="text-berti-gold text-[10px] font-bold uppercase tracking-widest">Digital</h4>
          <div className="flex gap-6">
            <a aria-label="Instagram da Berti Construtora" href="https://instagram.com/construtora.berti" target="_blank" rel="noreferrer" className="p-3 border border-white/10 text-white/30 hover:text-berti-gold hover:border-berti-gold transition-all duration-500">
               <Instagram size={20} />
            </a>
          </div>
        </div>
      </div>
    </div>
    
    <div className="max-w-7xl mx-auto pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-start md:items-center gap-8 text-white/60 text-[9px] uppercase tracking-[0.2em] font-medium">
      <div className="space-y-3">
        <div className="text-white/80 font-bold tracking-[0.3em]">BERTI CONSTRUTORA LTDA</div>
        <div className="flex flex-col gap-1">
          <div>CNPJ: 20.425.894/0001-79</div>
          <div className="text-[10px] normal-case tracking-normal text-white/70">Rua Mateus Leme, 1970 — Centro Cívico</div>
          <div className="text-[10px] normal-case tracking-normal text-white/70">Curitiba/PR — CEP 80.530-010</div>
        </div>
      </div>
      <div className="flex flex-col md:items-end gap-2 text-right">
        <div className="flex gap-8">
           <span>Curitiba | Paraná</span>
        </div>
        <div className="text-white/70 text-[8px] mt-2">© {new Date().getFullYear()} Todos os direitos reservados.</div>
      </div>
    </div>
  </footer>
);

// --- Page Components ---

const InstitutionalLanding = ({ sections, projects, onProjectClick, testimonials, loading, concepts }: any) => {
  return (
    <div className="bg-berti-light text-berti-ink">
      {/* 1. Hero Section - Alto Padrão + Técnica */}
      <section ref={sections.hero} className="relative min-h-[100vh] flex flex-col items-center justify-end overflow-hidden pb-24 md:pb-32">
        <div className="absolute inset-0 z-0">
          <HeroBackground 
            src="https://curitibaconstrutora.com.br/assets/recovered-20261029/bc1289a23ae6.webp" 
            alt="Arquitetura de Alto Padrão Berti" 
            priority={true}
          />
          <div className="absolute inset-0 bg-berti-ink/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-berti-ink via-transparent to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl w-full px-6 md:px-12 flex flex-col items-center text-center mt-auto">
          <div className="max-w-5xl flex flex-col items-center w-full">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-[4.5rem] leading-[1.1] tracking-tight mb-10 font-bold text-white uppercase"
            >
              Menos improviso.<br />
              Mais controle na sua obra.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="text-lg md:text-xl lg:text-2xl text-white/90 max-w-4xl mb-16 font-light leading-relaxed mx-auto drop-shadow-md"
            >
              Você não precisa entender de obra.<br/>
              Precisa de alguém que controle cada etapa e conduza suas decisões com segurança.
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="flex flex-col md:flex-row gap-4 justify-center w-full max-w-3xl"
            >
              <button 
                onClick={() => window.location.href = `https://wa.me/5541991836651`}
                className="flex-1 px-10 py-5 bg-berti-gold text-berti-ink font-bold text-[11px] uppercase tracking-[0.3em] hover:bg-white transition-all duration-300 shadow-xl"
              >
                Falar no Whatsapp
              </button>
              <Link
                to="/jornada"
                className="flex-1 px-10 py-5 bg-white text-berti-ink font-bold text-[11px] uppercase tracking-[0.3em] hover:bg-gray-100 transition-all duration-300 shadow-xl flex items-center justify-center"
              >
                Como funciona
              </Link>
              <Link
                to="/portfolio"
                className="flex-1 px-10 py-5 border border-white/40 text-white font-bold text-[11px] uppercase tracking-[0.3em] hover:bg-white hover:text-berti-ink transition-all duration-300 flex items-center justify-center gap-3 bg-berti-ink/20 backdrop-blur-sm"
              >
                Ver Portfólio
              </Link>
            </motion.div>
          </div>
        </div>

        <motion.div 
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2 opacity-40 text-white flex flex-col items-center gap-4 cursor-pointer"
        >
          <div className="w-[1px] h-20 bg-white" />
        </motion.div>
      </section>

      {/* 2. Como Funciona (Processo) */}
      <section className="py-40 bg-berti-light border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 items-start">
            <div className="lg:col-span-5 sticky top-32">
              <div className="text-berti-gold-dark text-xs font-bold uppercase tracking-[0.4em] mb-8">Processo de Gestão</div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl mb-10 leading-[1] tracking-tight">Como a Berti <br />conduz seu projeto</h2>
              <p className="text-xl text-gray-800 leading-relaxed font-light mt-6">
                Toda obra segue etapas. <br />
                <span className="font-medium text-berti-ink">O problema é quando elas não conversam entre si.</span>
              </p>
              <p className="text-lg text-gray-500 leading-relaxed font-light mt-6 border-l-2 border-berti-gold/30 pl-6">
                Nosso papel é integrar projeto, suprimentos, finanças e execução no canteiro de obras para que nada saia do controle.
              </p>
            </div>
            
            <div className="lg:col-span-1 hidden lg:block"></div>

            <div className="lg:col-span-6 space-y-4">
              {[
                { step: "01", title: "Análise técnica completa do projeto", desc: "Antes de iniciar, verificamos projetos de engenharia e arquitetura em busca de interferências e otimizações executivas." },
                { step: "02", title: "Orçamento e cronograma estruturados", desc: "Definição do fluxo físico-financeiro real da obra para alinhar os desembolsos ao escopo construtivo." },
                { step: "03", title: "Coordenação ativa das equipes", desc: "Mobilização e sincronização contínua das frentes de trabalho para evitar gargalos entre etapas produtivas." },
                { step: "04", title: "Controle logístico de suprimentos", desc: "Equalização de propostas, compras com mapa de cotação e validação do recebimento diretamente no canteiro." },
                { step: "05", title: "Condução técnica até a entrega", desc: "Direcionamento constante de método construtivo, normativas e fiscalização da qualidade em cada avanço de etapa." },
                { step: "06", title: "Transparência e prestação de contas", desc: "Fechamentos mensais detalhados de custo consolidado, fotos de avanço e controle documental impecável." }
              ].map((item, i) => (
                <div key={i} className="group border border-gray-100 bg-white p-8 hover:border-berti-gold/30 hover:shadow-lg transition-all cursor-default relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-8 opacity-[0.02] text-8xl font-black group-hover:text-berti-gold transition-colors">{item.step}</div>
                  <div className="flex flex-col gap-4 relative z-10">
                    <div className="text-berti-gold-dark font-display font-black text-2xl">{item.step}</div>
                    <h3 className="text-2xl font-bold tracking-tight text-berti-ink pr-12">{item.title}</h3>
                    <p className="text-gray-500 leading-relaxed font-light">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 3. Problema + Posicionamento */}
      <section ref={sections.pos} className="py-40 px-6 md:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-24 items-center">
             <div className="md:col-span-4 aspect-square bg-berti-ink p-16 flex flex-col justify-between shadow-2xl relative overflow-hidden">
                <div className="absolute -bottom-8 -right-8 text-[15rem] leading-none text-white/5 font-serif italic">B</div>
                <div className="text-berti-gold font-display text-4xl italic mb-auto">B<span className="text-white">.</span></div>
                <div className="space-y-6 relative z-10">
                   <div className="h-px w-12 bg-berti-gold/50"></div>
                   <h3 className="text-white text-5xl font-extrabold leading-[1.1] tracking-tight">O que fazemos</h3>
                </div>
             </div>
             <div className="md:col-span-8">
                <div className="text-berti-gold-dark text-xs font-bold uppercase tracking-[0.4em] mb-12">Nossa Atuação</div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl mb-12 leading-[1.1] tracking-tight text-berti-ink">A maioria das obras perde o controle porque não existe condução técnica clara.</h2>
                <div className="space-y-8">
                  <p className="text-xl text-gray-600 font-light leading-relaxed">
                    A Berti entra exatamente nesse ponto: <span className="font-medium text-berti-ink">organizar, estruturar e garantir que cada decisão seja tomada com base técnica</span> — do início ao fim.
                  </p>
                  <p className="text-xl text-gray-500 font-light leading-relaxed">
                    Não somos apenas executores, agimos como os curadores técnicos do investimento do cliente no processo de construção.
                  </p>
                </div>
             </div>
          </div>
        </div>
      </section>



      {/* 5. Transparência */}
      <section className="py-40 bg-white border-b border-gray-100 overflow-hidden relative">
        <div className="absolute top-0 right-0 p-32 opacity-[0.02] text-[25rem] font-black leading-none pointer-events-none select-none uppercase -translate-y-1/4 translate-x-1/4 text-berti-ink">Trust</div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col lg:flex-row gap-24 items-center relative z-10">
          <div className="lg:w-1/2">
            <div className="text-berti-gold-dark text-xs font-bold uppercase tracking-[0.4em] mb-12">Transparência Total</div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl mb-10 leading-[1] font-black text-berti-ink tracking-tight">Sem margem escondida. <br /><span className="text-gray-500 font-light italic font-serif">Sem ruído.</span></h2>
            <p className="text-xl text-gray-600 mb-10 font-light leading-relaxed max-w-xl">
              Você sabe exatamente para onde está indo cada decisão e cada investimento.
            </p>
            <p className="text-lg text-gray-500 mb-16 font-light leading-relaxed max-w-xl">
              Nosso modelo privilegia orçamento claro e visibilidade real sobre a obra. A mão de obra é acompanhada por avanço técnico, materiais seguem cotação e o cliente acompanha a execução com critério.
            </p>
          </div>
          <div className="lg:w-1/2 w-full">
            <div className="bg-berti-light border border-gray-200 p-16 space-y-10">
              {[
                { label: "Orçamento estruturado por etapa construtiva", icon: Calculator },
                { label: "Acompanhamento técnico e fiscal rigoroso", icon: HardHat },
                { label: "Relatórios detalhados com fotos e medições", icon: FileText },
                { label: "Auditoria e organização de notas fiscais", icon: Receipt },
                { label: "Equalização e coordenação de cotações", icon: Scale },
                { label: "Decisão estratégica guiada por dados", icon: Target }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-6 group">
                  <div className="w-10 h-10 rounded shadow-sm bg-berti-gold/10 flex items-center justify-center shrink-0 group-hover:bg-berti-gold/20 transition-colors">
                    <item.icon size={18} className="text-berti-gold-dark" />
                  </div>
                  <div className="text-gray-700 font-medium group-hover:text-berti-ink transition-colors leading-snug">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 6. Escopo de Atuação */}
      <section className="py-40 bg-berti-ink relative">
        <div className="absolute inset-0 z-0 flex overflow-hidden opacity-10 pointer-events-none">
          <motion.div 
            animate={{ x: ["0%", "-50%"] }} 
            transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
            className="flex w-[200%] h-full"
          >
            {[...(concepts && concepts.length > 0 ? concepts : [
              "https://curitibaconstrutora.com.br/assets/recovered-20261029/09983b29ba41.webp",
              "https://curitibaconstrutora.com.br/assets/recovered-20261029/9e78b333759f.webp",
              "https://curitibaconstrutora.com.br/assets/recovered-20261029/d7bd32fd882e.webp",
              "https://curitibaconstrutora.com.br/assets/recovered-20261029/a7ba6c524485.webp"
            ]), ...(concepts && concepts.length > 0 ? concepts : [
              "https://curitibaconstrutora.com.br/assets/recovered-20261029/09983b29ba41.webp",
              "https://curitibaconstrutora.com.br/assets/recovered-20261029/9e78b333759f.webp",
              "https://curitibaconstrutora.com.br/assets/recovered-20261029/d7bd32fd882e.webp",
              "https://curitibaconstrutora.com.br/assets/recovered-20261029/a7ba6c524485.webp"
            ])].map((src, i) => (
              <div key={i} className="flex-none h-full w-[50vw] md:w-[33vw] lg:w-[25vw] shrink-0 border-r border-white/5">
                <img 
                  {...responsiveImageProps(src as string, "(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw")}
                  decoding="async"
                  alt="" 
                  className="w-full h-full object-cover grayscale" 
                  loading="lazy" 
                />
              </div>
            ))}
          </motion.div>
        </div>
        
        <div className="absolute inset-0 z-0 bg-gradient-to-br from-berti-ink via-berti-ink/95 to-berti-ink/80 pointer-events-none" />

        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 flex flex-col items-center text-center">
            <div className="text-berti-gold text-xs font-bold uppercase tracking-[0.4em] mb-8">Escopo Completo</div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl mb-12 leading-[1.1] text-white tracking-tight">Da fundação ao acabamento, <br /><span className="text-berti-sage font-light italic font-serif">com coordenação técnica.</span></h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-12 mt-10 w-full max-w-5xl">
              {[
                "Integração de Projetos",
                "Controle Estrutural",
                "Gestão de Instalações",
                "Curadoria de Acabamentos"
              ].map(t => (
                <div key={t} className="text-white/90 text-[11px] sm:text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-4">
                  <div className="w-1.5 h-1.5 bg-berti-gold"></div>
                  {t}
                </div>
              ))}
            </div>
        </div>
      </section>



      {/* 9. CTA Final */}
      <section className="py-40 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
           <div className="flex flex-col lg:flex-row gap-24 items-center justify-between">
              <div className="max-w-3xl">
                 <div className="text-berti-gold-dark text-xs font-bold uppercase tracking-[0.4em] mb-12 flex items-center gap-6">
                    <span className="w-16 h-px bg-berti-gold-dark"></span>
                    Próximo Passo
                 </div>
                 <h2 className="text-5xl md:text-6xl lg:text-7xl leading-[1] font-bold tracking-tight mb-12 text-berti-ink">Envie seu projeto. <br /><span className="text-berti-gold-dark italic font-serif font-light">A Berti analisa.</span></h2>
                 <p className="text-xl text-gray-700 font-light leading-relaxed mb-6">
                   O próximo passo para uma obra organizada e previsível é uma análise técnica clara.
                 </p>
                 <p className="text-lg text-gray-500 font-light leading-relaxed mb-16">
                   Se você busca controle desde a primeira decisão, nós podemos guiar o processo construtivo. Arquitetura, orçamento, execução e tomada de decisão precisam falar a mesma língua.
                 </p>
                 
                 <div className="flex flex-col sm:flex-row gap-6 w-full lg:w-auto">
                    <button 
                     onClick={() => window.location.href = `https://wa.me/5541991836651`}
                     className="px-10 py-5 bg-berti-ink text-white font-bold text-[11px] uppercase tracking-[0.3em] hover:bg-berti-gold transition-all duration-500 shadow-xl flex-1 md:flex-none"
                    >
                      Solicitar Análise
                    </button>
                    <button 
                     onClick={() => window.location.href = `mailto:berti@curitibaconstrutora.com.br`}
                     className="px-10 py-5 border border-gray-300 text-berti-ink font-bold text-[11px] uppercase tracking-[0.3em] hover:bg-gray-100 transition-all duration-500 flex-1 md:flex-none"
                    >
                      Enviar por E-mail
                    </button>
                 </div>
              </div>
           </div>
        </div>
      </section>
    </div>
  );
};

const ProjectCard = ({ project, onClick, idx }: any) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ delay: idx * 0.1, duration: 1, ease: [0.22, 1, 0.36, 1] }}
    viewport={{ once: true }}
    className="group cursor-pointer"
    onClick={onClick}
  >
    <div className="relative aspect-[3/4] overflow-hidden bg-gray-100 mb-8 border border-gray-100 shadow-sm transition-all duration-700 hover:shadow-2xl">
      <div className="absolute inset-0 bg-berti-dark z-10 opacity-0 group-hover:opacity-20 transition-opacity duration-700" />
      {project.photos?.[0] ? (
        <ParallaxImage 
          src={project.photos[0]} 
          className="w-full h-full object-cover transition-all duration-[2000ms] ease-out group-hover:scale-110 saturate-125 contrast-110" 
          alt={project.title}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-gray-400">
          <Building2 size={48} strokeWidth={0.5} />
        </div>
      )}
      <div className="absolute inset-0 z-20 flex flex-col justify-end p-8 opacity-0 group-hover:opacity-100 transition-all duration-700 translate-y-4 group-hover:translate-y-0 text-white">
        <span className="text-berti-gold text-[9px] font-bold uppercase tracking-widest mb-1">{project.location}</span>
        <h4 className="text-xl font-display font-medium italic">Explorar Projeto</h4>
      </div>
    </div>
    <div className="space-y-6">
      <div className="flex items-center justify-between">
         <h3 className="text-4xl font-extrabold tracking-tighter leading-none group-hover:text-berti-gold transition-colors italic">
           {project.title}
         </h3>
         <div className="text-[9px] font-bold uppercase tracking-widest text-berti-gold opacity-0 group-hover:opacity-100 transition-opacity duration-500">Ver Obra</div>
      </div>
      <div className="flex items-center justify-between pt-6 border-t border-gray-100 text-berti-ink/40 text-[9px] font-bold uppercase tracking-widest tracking-[0.2em]">
        <span>{project.location}</span>
        <div className="flex gap-4">
           <span>{project.year}</span>
           <span>{project.area}</span>
        </div>
      </div>
    </div>
  </motion.div>
);

const ProjectDetail = ({ project, onBack }: any) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const galleryImages = project.photos ? [...new Set<string>(project.photos)] : [];

  return (
    <div className="bg-[#FAF9F6] min-h-screen">
      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex flex-col items-center justify-center backdrop-blur-sm"
          >
            <button onClick={() => setLightboxIndex(null)} className="absolute top-6 right-6 text-white p-4 hover:bg-white/10 transition-colors z-50">
              <X size={32} />
            </button>
            
            {galleryImages.length > 1 && (
              <>
                <button onClick={() => setLightboxIndex(lightboxIndex > 0 ? lightboxIndex - 1 : galleryImages.length - 1)} className="absolute left-6 text-white p-4 hover:bg-white/10 transition-colors z-50">
                  <ArrowLeft size={32} />
                </button>
                <button onClick={() => setLightboxIndex(lightboxIndex < galleryImages.length - 1 ? lightboxIndex + 1 : 0)} className="absolute right-6 text-white p-4 hover:bg-white/10 transition-colors z-50">
                  <ArrowLeft size={32} className="rotate-180" />
                </button>
              </>
            )}
            
            <div className="relative w-full h-full p-12 md:p-24 flex items-center justify-center">
              <motion.img 
                key={lightboxIndex}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                src={galleryImages[lightboxIndex]} 
                className="max-w-full max-h-full object-contain"
                alt="Obra"
              />
            </div>
            
            <div className="absolute bottom-10 text-white/50 text-[10px] uppercase tracking-[0.5em] font-bold">
              {lightboxIndex + 1} / {galleryImages.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="pt-32 pb-6 px-6 md:px-16 bg-[#FAF9F6] border-b border-berti-ink/10">
        <button onClick={onBack} className="text-berti-ink text-[10px] font-bold uppercase tracking-[0.3em] flex items-center gap-3 py-3 hover:text-berti-gold-dark transition-colors">
          <ArrowLeft size={16} /> Voltar ao Portfólio
        </button>
      </div>
      <div className="relative h-[85vh] bg-berti-dark overflow-hidden">
        {project.photos?.[0] && (
          <ParallaxImage 
            src={project.photos[0]} 
            className="w-full h-full object-cover opacity-70 saturate-125 contrast-110"
            alt={project.title}
          />
        )}
        <div className="absolute inset-0 flex items-center justify-center text-center p-6 bg-gradient-to-t from-berti-dark/90 via-transparent to-transparent">
          <div className="max-w-5xl mt-20">

            <h2 className="text-white text-5xl md:text-[7rem] leading-[0.8] tracking-tightest mb-10 font-extrabold italic drop-shadow-2xl px-4">
              {project.title}
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              {project.area && <span className="text-[9px] font-bold uppercase tracking-[0.5em] text-white border border-white/20 px-8 py-4 bg-white/5 backdrop-blur-sm">{project.area}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-start">
          <div className="lg:col-span-7 space-y-16">
            <div className="text-berti-gold-dark text-[10px] font-bold uppercase tracking-[0.5em] flex items-center gap-4">
              <span className="w-8 h-px bg-berti-gold-dark"></span>
              Resumo Executivo
            </div>
            <p className="text-xl md:text-3xl text-gray-700 leading-[1.6] font-light">
              {project.description}
            </p>
          </div>
          
          <div className="lg:col-span-5 space-y-12">
            <div className="bg-white p-12 border border-berti-ink/10 shadow-sm relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-berti-slate/5 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-150 duration-700" />
              <div className="text-[10px] font-bold uppercase tracking-[0.5em] text-berti-gold-dark mb-10">Especificações</div>
              <div className="space-y-8 relative z-10">
                <TechnicalItem icon={<MapPin size={20} />} label="Local" value={project.location} />
                <TechnicalItem icon={<Calendar size={20} />} label={project.unitCount ? "Período das Obras" : "Ano de Entrega"} value={project.year} />
                {project.unitCount && <TechnicalItem icon={<Building2 size={20} />} label="Unidades Executadas" value={`${project.unitCount} lojas`} />}
                {(!project.unitCount || project.area) && <TechnicalItem icon={<Ruler size={20} />} label="Área Construída" value={project.area} />}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Storytelling Marquee for Execution Photos */}
      {project.executionPhotos && project.executionPhotos.length > 0 && (
        <div className="py-24 border-t border-gray-200 bg-white overflow-hidden">
          <div className="max-w-[1440px] mx-auto px-6 mb-12 flex items-center justify-between">
            <h3 className="text-xl md:text-3xl font-display font-extrabold tracking-tight text-berti-ink">Acompanhe cada etapa da obra</h3>
            <div className="hidden md:block text-[9px] font-bold uppercase tracking-[0.4em] text-gray-400">Linha do Tempo Executiva</div>
          </div>
          <div className="relative flex overflow-x-hidden w-full">
            <motion.div 
              animate={{ x: ["0%", "-50%"] }} 
              transition={{ repeat: Infinity, ease: "linear", duration: 180 }}
              style={{ willChange: "transform", transform: "translateZ(0)" }}
              className="flex gap-4 w-max"
            >
              {[...project.executionPhotos, ...project.executionPhotos, ...project.executionPhotos, ...project.executionPhotos].map((url: string, i: number) => (
                <div key={i} className="flex-none w-48 md:w-64 aspect-square overflow-hidden bg-gray-100">
                  <img 
                    src={url} 
                    className="w-full h-full object-cover opacity-100 hover:scale-105 transition-all duration-700 pointer-events-none" 
                    alt="Acompanhamento obra" 
                  />
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      )}

      {/* Main Gallery Masonry */}
      {galleryImages.length > 0 && (
        <div className="max-w-[1440px] mx-auto px-6 py-24 md:py-40">
          <div className="text-[9px] font-bold uppercase tracking-[0.5em] text-gray-400 border-b border-gray-200 pb-6 mb-16 w-full flex justify-between items-center">
            <span>Registro Fotográfico Oficial</span>
            <span>{galleryImages.length} Imagens</span>
          </div>
          <Masonry
            breakpointCols={{ default: 3, 1024: 2, 640: 1 }}
            className="flex -ml-6 w-auto"
            columnClassName="pl-6 bg-clip-padding"
          >
            {galleryImages.map((url: string, i: number) => (
              <div key={i} onClick={() => setLightboxIndex(i)} className="mb-6 overflow-hidden bg-gray-50 shadow-sm relative group cursor-zoom-in">
                <img 
                  src={url} 
                  className="w-full h-auto block object-cover contrast-105 transition-transform duration-[2000ms] group-hover:scale-[1.03]" 
                  alt="" 
                  loading="lazy"
                />
                <div className="absolute inset-0 ring-1 ring-inset ring-black/5 pointer-events-none" />
                <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors duration-500 pointer-events-none" />
              </div>
            ))}
          </Masonry>
        </div>
      )}

      {/* Direct Conversion Block */}
      <div className="bg-berti-ink py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
            <h4 className="font-display text-4xl md:text-5xl font-extrabold italic text-white mb-6">Conversão Direta</h4>
            <p className="text-gray-400 text-base md:text-lg leading-relaxed font-light mb-10 max-w-xl mx-auto">
              Solicite um estudo de viabilidade para seu projeto com os mesmos padrões de engenharia Berti.
            </p>
            <button onClick={() => window.open('https://wa.me/5541991836651', '_blank')} className="inline-block py-6 px-12 bg-white text-berti-ink text-[11px] font-bold uppercase tracking-[0.4em] hover:bg-berti-gold transition-colors">
              Iniciar Atendimento
            </button>
        </div>
      </div>
    </div>
  );
};

const TechnicalItem = ({ icon, label, value }: any) => (
  <div className="flex items-center gap-6 group">
    <div className="p-4 bg-white text-berti-sage shadow-md rounded-sm group-hover:bg-berti-gold group-hover:text-white transition-all">{icon}</div>
    <div>
      <div className="text-[10px] text-gray-400 uppercase font-bold tracking-widest mb-1">{label}</div>
      <div className="font-bold text-base">{value || 'Consulte'}</div>
    </div>
  </div>
);

const AdminLogin = ({ onLogin, onBack }: any) => (
  <div className="bg-berti-ink p-16 max-w-md w-full shadow-2xl shadow-berti-ink/50 text-center relative border border-white/5">
    <div className="absolute top-0 left-0 w-full h-1 bg-berti-gold"></div>
    <div className="text-berti-gold mb-8"><Lock size={40} className="mx-auto" strokeWidth={1.5} /></div>
    <div className="font-display text-4xl mb-4 text-white">Área do Cliente</div>
    <p className="text-white/50 text-[10px] mb-12 uppercase tracking-[0.2em] leading-relaxed font-bold">
      Acesso exclusivo para clientes e engenharia
    </p>
    <button 
      onClick={onLogin}
      className="w-full flex items-center justify-center gap-4 py-6 bg-berti-gold text-berti-ink hover:bg-white hover:text-berti-ink transition-all text-[11px] font-bold uppercase tracking-widest"
    >
      Acessar com Google
    </button>
    <button 
      onClick={onBack}
      className="mt-10 text-[10px] font-bold uppercase tracking-[0.2em] text-white/30 hover:text-berti-gold transition-colors cursor-pointer w-full text-center"
    >
      Retornar ao Institucional
    </button>
  </div>
);

const ClientDashboard = ({ user, onLogout }: any) => (
  <div className="bg-white p-12 max-w-md w-full shadow-2xl text-center border-t-4 border-berti-gold">
    <div className="text-berti-gold mb-8"><Lock size={48} className="mx-auto" /></div>
    <div className="font-display text-2xl mb-4 text-berti-ink">Acesso Restrito</div>
    <p className="text-gray-500 text-sm mb-12 leading-relaxed font-light">
      Olá, <strong>{user.displayName || user.email}</strong>. <br /><br />
      Este e-mail não possui cadastro ou autorização ativa em nosso sistema. Caso você seja cliente, por favor, entre em contato conosco para solicitar a liberação do seu acesso à Área do Cliente.
    </p>
    <button onClick={onLogout} className="w-full py-4 bg-berti-dark text-white text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-berti-sage transition-all">Sair da Conta</button>
  </div>
);

const ClientJourneyPage = ({ images }: any) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const defaultImages = [
    "https://curitibaconstrutora.com.br/assets/recovered-20261029/7c9d0f511a7e.webp",
    "https://curitibaconstrutora.com.br/assets/recovered-20261029/bc1289a23ae6.webp",
    "https://curitibaconstrutora.com.br/assets/recovered-20261029/d7bd32fd882e.webp",
    "https://curitibaconstrutora.com.br/assets/recovered-20261029/9e78b333759f.webp",
    "https://curitibaconstrutora.com.br/assets/recovered-20261029/af23aefb7f1e.webp"
  ];
  const displayImages = defaultImages.map((img, i) => (images && images[i]) ? images[i] : img);
  
  const phases = [
    { title: "Diagnóstico", subtitle: "Passos 01, 02 e 03" },
    { title: "Estruturação", subtitle: "Passos 04, 05 e 06" },
    { title: "Planejamento", subtitle: "Passos 07, 08 e 09" },
    { title: "Execução e Entrega", subtitle: "Passos 10, 11 e 12" }
  ];

  const journeyImagesList = displayImages.slice(0, 4);
  const servicesImage = displayImages[4];

  const [activeIndex, setActiveIndex] = useState(0);

  const nextPhase = () => {
    setActiveIndex((prev) => (prev + 1) % journeyImagesList.length);
  };

  const prevPhase = () => {
    setActiveIndex((prev) => (prev - 1 + journeyImagesList.length) % journeyImagesList.length);
  };

  return (
    <div 
      className="min-h-screen bg-berti-light pt-32 pb-40 overflow-x-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl mx-auto"
        >
          <div className="text-berti-gold text-[10px] font-bold uppercase tracking-[0.5em] mb-6 flex items-center justify-center gap-4">
             <span className="w-8 h-px bg-berti-gold/40"></span>
             A JORNADA DA OBRA
             <span className="w-8 h-px bg-berti-gold/40"></span>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl mb-8 font-display tracking-tight text-berti-ink">
            A jornada da sua obra, <br className="hidden md:block" />
            <span className="italic font-serif font-light text-berti-sage">conduzida com clareza.</span>
          </h1>
          <p className="text-lg md:text-xl text-berti-ink/60 font-light max-w-3xl mx-auto leading-relaxed">
            Do primeiro contato à entrega das chaves, cada etapa é organizada para garantir previsibilidade, segurança e controle sobre o seu investimento.
          </p>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Navigation Tabs (Desktop & Tablet) */}
        <div className="hidden md:flex justify-between items-center mb-10 max-w-5xl mx-auto border-b border-gray-200">
          {phases.map((phase, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`flex-1 pb-6 relative group transition-all duration-300 ${
                activeIndex === index ? 'text-berti-gold' : 'text-berti-ink/50 hover:text-berti-ink'
              }`}
            >
              <div className="flex flex-col items-center gap-2">
                <span className="text-[11px] font-bold uppercase tracking-widest">0{index + 1} {phase.title}</span>
                <span className="text-xs font-light">{phase.subtitle}</span>
              </div>
              {activeIndex === index && (
                <motion.div 
                  layoutId="activeTabDesktop"
                  className="absolute bottom-0 left-0 right-0 h-[2px] bg-berti-gold"
                />
              )}
            </button>
          ))}
        </div>
        
        {/* Mobile Navigation Tabs (Scrollable) */}
        <div className="flex md:hidden overflow-x-auto snap-x hide-scrollbar mb-8 -mx-6 px-6 gap-6">
          {phases.map((phase, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`flex-none w-[70vw] snap-center pb-4 relative transition-all duration-300 ${
                activeIndex === index ? 'text-berti-gold' : 'text-berti-ink/50'
              }`}
            >
              <div className="flex flex-col items-start gap-1">
                 <span className="text-[10px] font-bold uppercase tracking-widest whitespace-nowrap">0{index + 1} {phase.title}</span>
                 <span className="text-[10px] font-light">{phase.subtitle}</span>
              </div>
               {activeIndex === index && (
                <motion.div 
                  layoutId="activeTabMobile"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-berti-gold"
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Gallery Slider - Full Width */}
      <div className="relative w-full bg-white mb-32 overflow-hidden group">
        <div className="relative w-full h-[60vh] md:h-[80vh] flex items-center justify-center bg-gray-50/30">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, scale: 1.01 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.99 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 w-full h-full flex items-center justify-center p-0"
            >
              <img 
                src={journeyImagesList[activeIndex]} 
                alt={`Fase ${activeIndex + 1}: ${phases[activeIndex].title}`}
                className="w-full h-full object-contain"
              />
            </motion.div>
          </AnimatePresence>

          {/* Clickable Navigation Sides - High Accessibility UX */}
          <div className="absolute inset-0 flex z-10">
            <div 
              onClick={prevPhase}
              className="flex-1 cursor-w-resize flex items-center justify-start pl-8 opacity-0 hover:opacity-100 transition-opacity duration-300"
            >
               <motion.div 
                className="p-6 rounded-full bg-berti-ink/5 backdrop-blur-md text-berti-ink border border-berti-ink/10 hidden md:flex hover:bg-berti-ink hover:text-white transition-all shadow-xl"
               >
                 <ArrowLeft className="w-8 h-8" />
               </motion.div>
            </div>
            <div 
              onClick={nextPhase}
              className="flex-1 cursor-e-resize flex items-center justify-end pr-8 opacity-0 hover:opacity-100 transition-opacity duration-300"
            >
               <motion.div 
                className="p-6 rounded-full bg-berti-ink/5 backdrop-blur-md text-berti-ink border border-berti-ink/10 hidden md:flex hover:bg-berti-ink hover:text-white transition-all shadow-xl"
               >
                 <ChevronRight className="w-8 h-8" />
               </motion.div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
         {/* CTA Section */}
         <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto text-center bg-white p-12 md:p-24 rounded-[3rem] shadow-2xl shadow-berti-dark/5 border border-gray-100 relative overflow-hidden mb-32"
         >
            {/* Subtle background decoration */}
            <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
              <ShieldCheck className="w-72 h-72 text-berti-gold" />
            </div>

            <div className="relative z-10">
              <h3 className="text-3xl md:text-5xl lg:text-6xl font-display tracking-tight text-berti-ink mb-8">
                Quer entender em qual etapa <br className="hidden md:block"/>está o seu projeto?
              </h3>
              <p className="text-lg md:text-xl text-berti-ink/60 font-light mb-12 max-w-3xl mx-auto leading-relaxed">
                Fale com a Berti e receba uma orientação inicial sobre o melhor caminho para estruturar sua obra com tranquilidade.
              </p>
              <button 
                onClick={() => window.open('https://wa.me/5541991836651', '_blank')}
                className="inline-flex items-center gap-6 px-12 py-6 bg-berti-dark text-white font-bold text-[12px] uppercase tracking-[0.2em] hover:bg-berti-gold transition-all duration-500 group shadow-2xl shadow-berti-dark/20"
              >
                <Phone className="w-4 h-4 text-berti-gold group-hover:text-white transition-colors" />
                Iniciar diagnóstico do projeto
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
         </motion.div>

         {/* Nossos Serviços Section */}
         <div className="space-y-24 mb-32">
            <motion.div 
               initial={{ opacity: 0, y: 20 }}
               whileInView={{ opacity: 1, y: 0 }}
               viewport={{ once: true }}
               className="text-center max-w-3xl mx-auto"
            >
               <div className="text-berti-gold-dark text-[10px] font-bold uppercase tracking-[0.5em] mb-6">SOLUÇÕES COMPLETAS</div>
               <h2 className="text-5xl md:text-7xl font-display tracking-tight text-berti-ink mb-8">Nossos Serviços</h2>
               <p className="text-lg text-berti-ink/60 font-light leading-relaxed">
                  Oferecemos um escopo completo de gestão e execução para que você tenha total segurança em todas as etapas da construção.
               </p>
            </motion.div>

            <motion.div 
               initial={{ opacity: 0, y: 30 }}
               whileInView={{ opacity: 1, y: 0 }}
               viewport={{ once: true }}
               transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
               className="relative h-[500px] md:h-[700px] overflow-hidden rounded-[2rem] md:rounded-[4rem] mx-auto max-w-7xl"
            >
               <img 
                  src={servicesImage} 
                  alt="Nossos Serviços Berti" 
                  className="w-full h-full object-cover"
               />
            </motion.div>

            <motion.div
               initial={{ opacity: 0, y: 20 }}
               whileInView={{ opacity: 1, y: 0 }}
               viewport={{ once: true }}
               className="max-w-7xl mx-auto w-full pt-12"
            >
               <div className="grid grid-cols-1 md:grid-cols-3 gap-16 text-berti-ink">
                  <div className="space-y-6">
                     <div className="w-12 h-px bg-berti-gold"></div>
                     <h4 className="text-xl font-bold tracking-tight">Gestão de Obras</h4>
                     <p className="text-base font-light leading-relaxed text-berti-ink/60">Controle rigoroso de cronogramas, custos e fornecedores com total transparência e relatórios detalhados.</p>
                  </div>
                  <div className="space-y-6">
                     <div className="w-12 h-px bg-berti-gold"></div>
                     <h4 className="text-xl font-bold tracking-tight">Execução Técnica</h4>
                     <p className="text-base font-light leading-relaxed text-berti-ink/60">Mão de obra especializada e engenharia de alta performance em cada etapa do processo construtivo.</p>
                  </div>
                  <div className="space-y-6">
                     <div className="w-12 h-px bg-berti-gold"></div>
                     <h4 className="text-xl font-bold tracking-tight">Consultoria Estratégica</h4>
                     <p className="text-base font-light leading-relaxed text-berti-ink/60">Análise técnica aprofundada, orçamentação precisa e estudos de viabilidade para investimentos seguros.</p>
                  </div>
               </div>
            </motion.div>
         </div>
      </div>
    </div>
  );
};

// --- Main App Logic ---

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const [activeProject, setActiveProject] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [concepts, setConcepts] = useState<any[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [journeyImages, setJourneyImages] = useState<string[]>([
    "https://curitibaconstrutora.com.br/assets/recovered-20261029/7c9d0f511a7e.webp",
    "https://curitibaconstrutora.com.br/assets/recovered-20261029/bc1289a23ae6.webp",
    "https://curitibaconstrutora.com.br/assets/recovered-20261029/d7bd32fd882e.webp",
    "https://curitibaconstrutora.com.br/assets/recovered-20261029/9e78b333759f.webp",
    "https://curitibaconstrutora.com.br/assets/recovered-20261029/af23aefb7f1e.webp"
  ]);
  const [loading, setLoading] = useState(true);
  const [showSplash, setShowSplash] = useState(true);

  // Intro Splash Timer
  useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 600);
    return () => clearTimeout(timer);
  }, []);

  // Section Refs
  const heroRef = useRef<HTMLDivElement>(null);
  const posRef = useRef<HTMLDivElement>(null);
  const portfolioRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as any });
  }, [pathname]);

  const isAdminRoute = pathname === '/admin';
  const [authError, setAuthError] = useState(false);

  useEffect(() => {
    if (!isAdminRoute) return;
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    setLoading(true);
    setAuthError(false);

    loadAuth().then(({ auth, onAuthStateChanged, db, doc, getDoc, setDoc }) => {
      if (cancelled) return;
      unsubscribe = onAuthStateChanged(auth, async (u) => {
        try {
          if (cancelled) return;
          setUser(u);
          if (u) {
            const adminDoc = await getDoc(doc(db, 'admins', u.uid));
            if (cancelled) return;
            const isMasterAdmin = u.email === 'berti@curitibaconstrutora.com.br';
            setIsAdmin(adminDoc.exists() || isMasterAdmin);
            if (isMasterAdmin && !adminDoc.exists()) {
              try { await setDoc(doc(db, 'admins', u.uid), { role: 'admin', email: u.email }); } catch (e) {}
            }
          } else { setIsAdmin(false); }
        } catch (error) {
          console.error('Falha ao verificar acesso administrativo', error);
          if (!cancelled) { setIsAdmin(false); setAuthError(true); }
        } finally {
          if (!cancelled) setLoading(false);
        }
      });
    }).catch((error) => {
      console.error('Falha ao carregar autenticação', error);
      if (!cancelled) { setAuthError(true); setLoading(false); }
    });
    return () => { cancelled = true; unsubscribe?.(); };
  }, [isAdminRoute]);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    // Public markup paints independently of the database SDK.
    import('./lib/siteData').then(({ subscribePublicData }) => {
      if (cancelled) return;
      unsubscribe = subscribePublicData({
        setProjects, setProjectsLoading, setTestimonials, setTestimonialsLoading,
        setConcepts, setJourneyImages
      });
    }).catch((error) => {
      console.error('Falha ao carregar dados públicos', error);
      if (!cancelled) { setProjectsLoading(false); setTestimonialsLoading(false); }
    });
    return () => { cancelled = true; unsubscribe?.(); };
  }, []);

  const handleProjectClick = (p: Project) => {
    setActiveProject(p);
    navigate(`/portfolio/${p.id}`);
  };

  const handleLogin = async () => {
    try { 
      const { auth, signInWithPopup, GoogleAuthProvider } = await loadAuth();
      await signInWithPopup(auth, new GoogleAuthProvider()); 
    } catch (err: any) {
      console.error("Login failed:", err);
      if (err.code === 'auth/popup-blocked') {
        alert('O popup de login foi bloqueado pelo seu navegador. Por favor, habilite popups para este site.');
      } else {
        alert('Erro ao realizar login: ' + err.message + '\n\nVerifique se o domínio está autorizado no Firebase.');
      }
    }
  };

  const handleLogout = async () => {
    const { auth, signOut } = await loadAuth();
    await signOut(auth);
  };

  return (
    <AnimatePresence mode="wait">
      {showSplash ? (
        <motion.div 
          key="splash"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="h-screen w-full flex items-center justify-center bg-berti-ink text-berti-gold fixed inset-0 z-[999]"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ 
              opacity: [0, 1, 0.5, 1, 0.5, 1],
              scale: [0.95, 1, 1, 1, 1, 1]
            }}
            transition={{ 
              duration: 0.6,
              times: [0, 0.2, 0.4, 0.6, 0.8, 1],
              ease: "easeInOut"
            }}
            className="flex flex-col items-center gap-4"
          >
            <div className="font-display text-6xl md:text-8xl tracking-tightest font-black">
              Berti<span className="text-white">.</span>
            </div>
            <motion.div 
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 0.4, width: 40 }}
              transition={{ delay: 0.5, duration: 1 }}
              className="h-px bg-berti-gold"
            />
          </motion.div>
        </motion.div>
      ) : (
        <motion.div 
          key="content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, ease: "easeInOut" }}
          className="min-h-screen selection:bg-berti-gold selection:text-white"
        >
          <Navbar onAdminClick={() => navigate('/admin')} />
          
          <main className="pt-0">
            <AnimatePresence mode="wait">
              <motion.div key={location.pathname} className="w-full">
                <Routes location={location}>
                  <Route path="/" element={
                    <PageTransition>
                      <InstitutionalLanding 
                        sections={{ hero: heroRef, pos: posRef, portfolio: portfolioRef }}
                        projects={projects}
                        onProjectClick={handleProjectClick}
                        testimonials={testimonials} 
                        loading={testimonialsLoading} 
                        concepts={concepts.map(c => c.url)}
                      />
                    </PageTransition>
                  } />
                  <Route path="/jornada" element={
                    <PageTransition>
                      <ClientJourneyPage images={journeyImages} />
                    </PageTransition>
                  } />
                  
                  <Route path="/portfolio" element={
                    <PageTransition>
                      <section className="bg-white py-40 px-6 md:px-12">
                        <div className="max-w-7xl mx-auto">
                          <header className="mb-32">
                            <div className="text-berti-gold-dark text-xs font-bold uppercase tracking-[0.5em] mb-10">Portfólio Berti</div>
                            <h2 className="text-6xl md:text-9xl mb-12 font-extrabold tracking-tightest leading-none">Portfólio de <br /><span className="text-berti-gold-dark">Obras.</span></h2>
                            <p className="text-xl text-gray-500 max-w-2xl font-light leading-relaxed">
                              Condução técnica e execução de projetos que exigem precisão. Em cada entrega, refletimos nosso compromisso com o resultado final.
                            </p>
                          </header>
                          
                          {projectsLoading ? <LoadingSpinner /> : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-24">
                              {projects.map((p, idx) => (
                                <ProjectCard key={p.id} project={p} onClick={() => handleProjectClick(p)} idx={idx} />
                              ))}
                            </div>
                          )}
                        </div>
                      </section>
                    </PageTransition>
                  } />

                  <Route path="/portfolio/:projectId" element={
                    <PageTransition>
                      <ProjectDetailWrapper projects={projects} loading={projectsLoading} onBack={() => navigate('/portfolio')} />
                    </PageTransition>
                  } />

                  <Route path="/admin" element={
                    <PageTransition>
                      <div className={!user ? "min-h-screen bg-berti-ink flex flex-col justify-center items-center py-24 px-6 relative" : "min-h-screen bg-berti-light/50 flex flex-col justify-center items-center py-24 px-6"}>
                        {!user && <div className="absolute inset-0 bg-[url('https://curitibaconstrutora.com.br/assets/recovered-20261029/bc1289a23ae6.webp')] bg-cover bg-center opacity-5"></div>}
                        <div className="relative z-10 w-full flex justify-center">
                          {loading ? <LoadingSpinner /> : authError ? <p role="alert">Não foi possível verificar o acesso. Recarregue a página para tentar novamente.</p> : !user ? <AdminLogin onLogin={handleLogin} onBack={() => navigate('/')} /> : isAdmin ? <Suspense fallback={<LoadingSpinner />}><AdminDashboard projects={projects} testimonials={testimonials} concepts={concepts} journeyImages={journeyImages} onLogout={handleLogout} onViewSite={() => navigate('/')} /></Suspense> : <ClientDashboard user={user} onLogout={handleLogout} />}
                        </div>
                      </div>
                    </PageTransition>
                  } />
                </Routes>
              </motion.div>
            </AnimatePresence>
          </main>

          <Footer onHome={() => navigate('/')} />
          <WhatsAppButton />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const ProjectDetailWrapper = ({ projects, loading, onBack }: any) => {
  const { projectId } = useParams();
  if (loading) return <LoadingSpinner />;
  const project = projects.find((p: any) => p.id === projectId);
  if (!project) return <div className="h-screen flex items-center justify-center">Obra não encontrada.</div>;
  return <ProjectDetail project={project} onBack={onBack} />;
};
